import {Sound} from '@pixi/sound';
import {Texture} from 'pixi.js';
import {ISkeletonData} from 'pixi-spine';
import {Loader, UpdateCallback} from '@gongxigames/framework/core/services/resourceLoader/Loader';
import {FontLoader, FontLoaderParams} from '@gongxigames/framework/core/services/resourceLoader/FontLoader';
import {setupSequenceMeshRegions} from '@gongxigames/framework/core/services/resourceLoader/SpineCustomParser';
import {IBrowser, IService, Resources, SystemComponentType} from '@gongxigames/framework/core/types';
import {CoreDI} from '@gongxigames/framework/core/configs';
import {Spine} from '@gongxigames/framework/core/fragments/display';
import {inject, injectable} from '@gongxigames/framework/core/installer';

@injectable()
export class ResourceLoader implements IService {
    public static NAME: symbol = CoreDI.ResourceLoader;
    public type: SystemComponentType = 'service';
    private _resources: Resources = {
        textures: {},
        sounds: {},
        spines: {}
    };

    @inject(CoreDI.Browser)
    protected _browser!: IBrowser;

    private _updateCallback?: UpdateCallback;
    private _callback?: Function;
    private _onErrorCallback?: Function;
    private _totalSize = 0;
    private _loaded = 0;
    private _loaders: Loader[] = [];

    public init(): void {
        this.reset();
    }

    public destroy(): void {
        this.reset();
    }

    public load(callback?: Function, updateCallback?: UpdateCallback, onErrorCallback?: Function): void {
        this._callback = callback;
        this._updateCallback = updateCallback;
        this._onErrorCallback = onErrorCallback;

        for (const loader of this._loaders) {
            this._totalSize += loader.size;

            loader.load();
        }
    }

    public addFont(params: FontLoaderParams, size: number): void {
        const loader = new FontLoader(
            params,
            size,
            this.onLoaderCompleted.bind(this),
            this.updatePercent.bind(this),
            this.onError.bind(this),
            this._browser
        );

        this._loaders.push(loader);
    }

    public getTexture(name: string): Texture {
        return this._resources.textures[name];
    }

    public getSound(name: string): Sound {
        return this._resources.sounds[name];
    }

    public getSpine(name: string): Spine {
        return new Spine(this._resources.spines[name]);
    }

    public getSpineSkeletonData(name: string): ISkeletonData {
        return this._resources.spines[name];
    }

    public isSpineLoaded(name: string): boolean {
        return !!this._resources.spines[name];
    }

    public setTexture(name: string, texture: Texture): void {
        this._resources.textures[name] = texture;
    }

    public setSound(name: string, sound: Sound): void {
        this._resources.sounds[name] = sound;
    }

    public setSpine(name: string, spine: ISkeletonData): void {
        setupSequenceMeshRegions(spine);
        spine.name = name;
        this._resources.spines[name] = spine;
    }

    public get resources(): Resources {
        return this._resources;
    }

    protected onError(): void {
        if (this._onErrorCallback) {
            this._onErrorCallback();
        }
    }

    private reset(): void {
        this._totalSize = 0;
        this._loaded = 0;
        this._loaders.length = 0;
    }

    private onLoaderCompleted(): void {
        this.updatePercent();

        if (++this._loaded === this._loaders.length) {
            this.reset();
            this._callback && this._callback();
        }
    }

    private updatePercent(): void {
        let loadedPercent = 0;

        for (const loader of this._loaders) {
            loadedPercent += loader.loadedSize;
        }

        this._updateCallback && this._updateCallback(loadedPercent / this._totalSize);
    }
}
